import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';

// Track interface matching the music player Track type
export interface Track {
  id: number | string;
  title: string;
  artistName: string;
  albumName?: string;
  spotifyUrl?: string;
  spotifyTrackId?: string;
  musicTrackId?: string;
  appleMusicUrl?: string;
  appleMusicId?: string;
  youtubeMusicUrl?: string;
  amazonMusicUrl?: string;
  tidalUrl?: string;
  deezerUrl?: string;
  previewUrl?: string;
  thumbnailUrl?: string;
}

interface MusicPlayerState {
  // Loaded playlist - what's being browsed in the modal
  tracks: Track[];
  currentTrackIndex: number;
  preferredService: string;
  roundId?: number;
  leagueId?: number;
  roundName?: string;
  leagueName?: string;
  roundPhase?: 'submission' | 'voting' | 'completed';

  // Active playlist - what's actually playing in the mini player
  activeTracks: Track[];
  activeTrackIndex: number;
  activeService: string;
  activeRoundId?: number;
  activeLeagueId?: number;
  activeRoundName?: string;
  activeLeagueName?: string;
  activeRoundPhase?: 'submission' | 'voting' | 'completed';

  // Playback state
  isPlaying: boolean;
  isMinimized: boolean;
  isLoading: boolean;

  // Radio mode
  isRadioMode: boolean;
  radioRoundId?: number;
  radioEpisodeName?: string;
  radioLeagueName?: string;
}

// Callback type for vote request from mini player
type VoteRequestCallback = (leagueId: number, roundId: number) => void;

// Callback type for playlist request from mini player
type PlaylistRequestCallback = (leagueId: number, roundId: number) => void;

interface MusicPlayerContextType extends MusicPlayerState {
  // Actions
  setPlaylist: (tracks: Track[], index: number, service: string, meta?: {
    roundId?: number;
    leagueId?: number;
    roundName?: string;
    leagueName?: string;
    roundPhase?: 'submission' | 'voting' | 'completed';
  }) => void;
  activatePlaylist: () => void; // Sync loaded playlist to active (when play is clicked)
  setTrackIndex: (index: number) => void;
  setActiveTrackIndex: (index: number) => void; // For changing tracks in active playlist
  setIsPlaying: (playing: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setIsMinimized: (minimized: boolean) => void;
  clearPlaylist: () => void;
  toggleMinimized: () => void;
  // Shared adapter management (keeps adapter alive across modal/mini player switches)
  sharedAdapter: React.MutableRefObject<any | null>;
  // Vote request - allows mini player to trigger voting modal directly
  requestVote: (leagueId: number, roundId: number) => void;
  registerVoteHandler: (callback: VoteRequestCallback | null) => void;
  // Playlist request - allows mini player to open playlist modal directly
  requestPlaylist: (leagueId: number, roundId: number) => void;
  registerPlaylistHandler: (callback: PlaylistRequestCallback | null) => void;
  // Radio mode
  activateRadio: (roundId: number, episodeName: string, leagueName: string, track?: Track) => void;
  updateRadioTrack: (track: Track) => void;
  clearRadio: () => void;
}

const STORAGE_KEY = 'yapzap_music_player_state';
const STORAGE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// Default/initial state
const initialState: MusicPlayerState = {
  // Loaded playlist
  tracks: [],
  currentTrackIndex: 0,
  preferredService: 'spotify',

  // Active playlist (what's playing)
  activeTracks: [],
  activeTrackIndex: 0,
  activeService: 'spotify',

  // Playback state
  isPlaying: false,
  isMinimized: true,
  isLoading: false,

  // Radio mode
  isRadioMode: false,
};

const MusicPlayerContext = createContext<MusicPlayerContextType | null>(null);

// Helper to load state from localStorage
const loadPersistedState = (): MusicPlayerState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    const timestamp = parsed.timestamp || 0;
    const now = Date.now();

    // Check if expired
    if (now - timestamp > STORAGE_EXPIRY) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Return state but:
    // 1. Force paused (don't auto-play on reload)
    // 2. Clear activeTracks - mini player should only show when user explicitly plays
    //    The loaded playlist (tracks) is kept so user can resume from the modal
    return {
      ...parsed.state,
      isPlaying: false, // Always start paused on reload
      isLoading: false,
      activeTracks: [], // Clear active tracks - mini player hidden until user plays
      activeTrackIndex: 0,
    };
  } catch (error) {
    console.warn('Failed to load persisted music player state:', error);
    return null;
  }
};

// Helper to save state to localStorage
const persistState = (state: MusicPlayerState) => {
  try {
    const data = {
      state,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to persist music player state:', error);
  }
};

export function MusicPlayerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MusicPlayerState>(() => {
    // Try to load persisted state on mount
    const persisted = loadPersistedState();
    return persisted || initialState;
  });

  // Shared adapter ref - keeps adapter alive across modal/mini player switches
  // This prevents constant recreation/destruction of Spotify/Apple Music adapters
  const sharedAdapterRef = useRef<any | null>(null);

  // Vote handler ref - allows league detail page to register a callback
  const voteHandlerRef = useRef<VoteRequestCallback | null>(null);

  // Playlist handler ref - allows league detail page to register a callback
  const playlistHandlerRef = useRef<PlaylistRequestCallback | null>(null);

  // Persist state changes to localStorage (debounced)
  useEffect(() => {
    // Only persist if we have tracks
    if (state.tracks.length > 0) {
      const timeoutId = setTimeout(() => {
        persistState(state);
      }, 500); // Debounce 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [state]);

  const setPlaylist = useCallback((
    tracks: Track[],
    index: number,
    service: string,
    meta?: {
      roundId?: number;
      leagueId?: number;
      roundName?: string;
      leagueName?: string;
      roundPhase?: 'submission' | 'voting' | 'completed';
    }
  ) => {
    setState(prev => ({
      ...prev,
      // Update loaded playlist (for browsing in modal)
      tracks,
      currentTrackIndex: index,
      preferredService: service,
      roundId: meta?.roundId,
      leagueId: meta?.leagueId,
      roundName: meta?.roundName,
      leagueName: meta?.leagueName,
      roundPhase: meta?.roundPhase,
      // Don't automatically activate - only when play is clicked
    }));
  }, []);

  // Activate the loaded playlist (called when user clicks play)
  // Can optionally pass tracks directly to avoid React state batching issues
  const activatePlaylist = useCallback((directTracks?: Track[], directIndex?: number, directService?: string, directMeta?: {
    roundId?: number;
    leagueId?: number;
    roundName?: string;
    leagueName?: string;
    roundPhase?: 'submission' | 'voting' | 'completed';
  }) => {
    setState(prev => {
      // If direct values provided, use them; otherwise use the loaded playlist from state
      const tracksToActivate = directTracks ?? prev.tracks;
      const indexToActivate = directIndex ?? prev.currentTrackIndex;
      const serviceToActivate = directService ?? prev.preferredService;

      return {
        ...prev,
        // Also update the loaded tracks if direct values provided
        tracks: directTracks ?? prev.tracks,
        currentTrackIndex: directIndex ?? prev.currentTrackIndex,
        preferredService: directService ?? prev.preferredService,
        roundId: directMeta?.roundId ?? prev.roundId,
        leagueId: directMeta?.leagueId ?? prev.leagueId,
        roundName: directMeta?.roundName ?? prev.roundName,
        leagueName: directMeta?.leagueName ?? prev.leagueName,
        roundPhase: directMeta?.roundPhase ?? prev.roundPhase,
        // Activate with the correct values
        activeTracks: tracksToActivate,
        activeTrackIndex: indexToActivate,
        activeService: serviceToActivate,
        activeRoundId: directMeta?.roundId ?? prev.roundId,
        activeLeagueId: directMeta?.leagueId ?? prev.leagueId,
        activeRoundName: directMeta?.roundName ?? prev.roundName,
        activeLeagueName: directMeta?.leagueName ?? prev.leagueName,
        activeRoundPhase: directMeta?.roundPhase ?? prev.roundPhase,
        isMinimized: false, // Expand when activating
      };
    });
  }, []);

  const setTrackIndex = useCallback((index: number) => {
    setState(prev => ({ ...prev, currentTrackIndex: index }));
  }, []);

  const setActiveTrackIndex = useCallback((index: number) => {
    setState(prev => ({ ...prev, activeTrackIndex: index }));
  }, []);

  const setIsPlaying = useCallback((playing: boolean) => {
    setState(prev => {
      // When playback starts and we have a loaded playlist but no active playlist,
      // automatically activate the playlist so the mini player appears
      if (playing && prev.tracks.length > 0 && prev.activeTracks.length === 0) {
        return {
          ...prev,
          isPlaying: playing,
          // Activate the loaded playlist
          activeTracks: prev.tracks,
          activeTrackIndex: prev.currentTrackIndex,
          activeService: prev.preferredService,
          activeRoundId: prev.roundId,
          activeLeagueId: prev.leagueId,
          activeRoundName: prev.roundName,
          activeLeagueName: prev.leagueName,
          activeRoundPhase: prev.roundPhase,
          isMinimized: false, // Expand when activating
        };
      }
      return { ...prev, isPlaying: playing };
    });
  }, []);

  const setIsLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setIsMinimized = useCallback((minimized: boolean) => {
    setState(prev => ({ ...prev, isMinimized: minimized }));
  }, []);

  const toggleMinimized = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: !prev.isMinimized }));
  }, []);

  const clearPlaylist = useCallback(() => {
    setState({ ...initialState, isRadioMode: false });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Register a vote handler (called by league detail page)
  const registerVoteHandler = useCallback((callback: VoteRequestCallback | null) => {
    voteHandlerRef.current = callback;
  }, []);

  // Request vote (called by mini player)
  const requestVote = useCallback((leagueId: number, roundId: number) => {
    if (voteHandlerRef.current) {
      voteHandlerRef.current(leagueId, roundId);
    }
  }, []);

  // Register a playlist handler (called by league detail page / music rounds display)
  const registerPlaylistHandler = useCallback((callback: PlaylistRequestCallback | null) => {
    playlistHandlerRef.current = callback;
  }, []);

  // Request playlist (called by mini player)
  const requestPlaylist = useCallback((leagueId: number, roundId: number) => {
    if (playlistHandlerRef.current) {
      playlistHandlerRef.current(leagueId, roundId);
    }
  }, []);

  // Radio mode methods
  const activateRadio = useCallback((roundId: number, episodeName: string, leagueName: string, track?: Track) => {
    setState(prev => ({
      ...prev,
      isRadioMode: true,
      radioRoundId: roundId,
      radioEpisodeName: episodeName,
      radioLeagueName: leagueName,
      isPlaying: true,
      isMinimized: false,
      // Set a dummy track so the mini player shows
      activeTracks: track ? [track] : [{ id: `radio-${roundId}`, title: episodeName, artistName: `YapZap Radio - ${leagueName}` }],
      activeTrackIndex: 0,
      activeService: 'radio',
    }));
  }, []);

  const updateRadioTrack = useCallback((track: Track) => {
    setState(prev => {
      if (!prev.isRadioMode) return prev;
      return {
        ...prev,
        activeTracks: [track],
        activeTrackIndex: 0,
      };
    });
  }, []);

  const clearRadio = useCallback(() => {
    setState(prev => ({
      ...prev,
      isRadioMode: false,
      radioRoundId: undefined,
      radioEpisodeName: undefined,
      radioLeagueName: undefined,
      activeTracks: [],
      activeTrackIndex: 0,
      activeService: 'spotify',
      isPlaying: false,
    }));
  }, []);


  const contextValue: MusicPlayerContextType = {
    ...state,
    setPlaylist,
    activatePlaylist,
    setTrackIndex,
    setActiveTrackIndex,
    setIsPlaying,
    setIsLoading,
    setIsMinimized,
    toggleMinimized,
    clearPlaylist,
    sharedAdapter: sharedAdapterRef,
    requestVote,
    registerVoteHandler,
    requestPlaylist,
    registerPlaylistHandler,
    activateRadio,
    updateRadioTrack,
    clearRadio,
  };

  return (
    <MusicPlayerContext.Provider value={contextValue}>
      {children}
    </MusicPlayerContext.Provider>
  );
}

// Custom hook to use the music player context
export function useMusicPlayer() {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    throw new Error('useMusicPlayer must be used within a MusicPlayerProvider');
  }
  return context;
}
